import React, { useState, useEffect } from 'react';
import { Save, Mail, CheckCircle, XCircle } from 'lucide-react';
import { apiService } from '../services/api';
import { User } from '../types';
import SessionToken from './SessionToken';
import './ProfileSettings.css';

const INDUSTRY_OPTIONS = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Manufacturing',
  'Retail',
  'Real Estate',
  'Consulting',
  'Non-profit',
  'Other'
];

const USE_CASE_OPTIONS = [
  'Lead Generation',
  'Market Research',
  'Competitive Analysis',
  'Brand Monitoring',
  'Content Strategy',
  'Sales Intelligence',
  'Other'
];

interface ProfileSettingsProps {
  user: User | null;
  onBack?: () => void;
  onUserUpdate?: (updatedUser: User) => void;
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ user, onBack, onUserUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [outlookStatus, setOutlookStatus] = useState<{
    connected: boolean;
    email?: string;
    needsReconnect?: boolean;
  } | null>(null);
  const [outlookLoading, setOutlookLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: user?.company || '',
    jobTitle: user?.jobTitle || '',
    industry: user?.industry || '',
    primaryUseCase: user?.primaryUseCase || '',
    goals: user?.goals || '',
    phone: user?.phone || '',
    linkedinUrl: user?.linkedinUrl || '',
    notes: user?.notes || '',
    dailySummaryEnabled: user?.dailySummaryEnabled ?? true
  });

  // Update form data when user prop changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        company: user.company || '',
        jobTitle: user.jobTitle || '',
        industry: user.industry || '',
        primaryUseCase: user.primaryUseCase || '',
        goals: user.goals || '',
        phone: user.phone || '',
        linkedinUrl: user.linkedinUrl || '',
        notes: user.notes || '',
        dailySummaryEnabled: user.dailySummaryEnabled ?? true
      });
    }
  }, [user]);

  // Check Outlook connection status on mount
  useEffect(() => {
    const checkOutlookStatus = async () => {
      try {
        const status = await apiService.getOutlookStatus();
        setOutlookStatus({
          connected: status.connected,
          email: status.profile?.email,
          needsReconnect: status.needsReconnect,
        });
      } catch (error) {
        console.error('Failed to check Outlook status:', error);
        setOutlookStatus({ connected: false });
      }
    };
    checkOutlookStatus();
  }, []);

  const useCaseOptions = formData.primaryUseCase && formData.primaryUseCase.trim() !== '' && !USE_CASE_OPTIONS.includes(formData.primaryUseCase)
    ? [formData.primaryUseCase, ...USE_CASE_OPTIONS]
    : USE_CASE_OPTIONS;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Check if there are any changes
    const originalData = {
      name: user?.name || '',
      email: user?.email || '',
      company: user?.company || '',
      jobTitle: user?.jobTitle || '',
      industry: user?.industry || '',
      primaryUseCase: user?.primaryUseCase || '',
      goals: user?.goals || '',
      phone: user?.phone || '',
      linkedinUrl: user?.linkedinUrl || '',
      notes: user?.notes || '',
      dailySummaryEnabled: user?.dailySummaryEnabled ?? true
    };
    
    const hasChanges = Object.keys(originalData).some(key => 
      formData[key as keyof typeof formData] !== originalData[key as keyof typeof originalData]
    );
    setHasChanges(hasChanges);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Update user profile
      const response = await apiService.updateUserProfile(formData);
      console.log('Profile update response:', response);
      
      // Call parent callback to update user data
      if (onUserUpdate) {
        console.log('Calling onUserUpdate with:', response.user);
        onUserUpdate(response.user);
      }
      
      setSuccess(true);
      setHasChanges(false);
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original user data
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      company: user?.company || '',
      jobTitle: user?.jobTitle || '',
      industry: user?.industry || '',
      primaryUseCase: user?.primaryUseCase || '',
      goals: user?.goals || '',
      phone: user?.phone || '',
      linkedinUrl: user?.linkedinUrl || '',
      notes: user?.notes || '',
      dailySummaryEnabled: user?.dailySummaryEnabled ?? true
    });
    setError(null);
    setHasChanges(false);
  };

  const handleConnectOutlook = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    window.location.href = `${apiUrl}/api/outlook/connect`;
  };

  const handleDisconnectOutlook = async () => {
    try {
      setOutlookLoading(true);
      await apiService.disconnectOutlook();
      setOutlookStatus({ connected: false });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to disconnect Outlook');
    } finally {
      setOutlookLoading(false);
    }
  };


  // Show loading state if user is not loaded yet
  if (!user) {
    return (
      <div className="profile-settings-container">
        <div className="profile-settings-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading profile...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-settings-wrapper">
      {/* Profile Info Card */}
      <div className="profile-info-card">
        <div className="profile-info-item">
          <span className="profile-info-value">{user.name || 'Not provided'}</span>
        </div>
        <span className="profile-info-separator">|</span>
        <div className="profile-info-item">
          <span className="profile-info-value">{user.company || 'Not provided'}</span>
        </div>
      </div>

      {/* Profile Details Card */}
      <div className="profile-settings-container">
        {/* Profile Content */}
        <div className="profile-settings-content">
          {/* Success Message */}
          {success && (
            <div className="success-message">
              Profile updated successfully!
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Profile Form */}
          <div className="profile-form">
          
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Professional Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="company">Company Name</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Company Name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="jobTitle">Job Title</label>
                <input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                  placeholder="Job Title"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="industry">Industry</label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleInputChange}
                >
                  <option value="">Industry</option>
                  {INDUSTRY_OPTIONS.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="primaryUseCase">Primary Use Case</label>
                <select
                  id="primaryUseCase"
                  name="primaryUseCase"
                  value={formData.primaryUseCase}
                  onChange={handleInputChange}
                >
                  <option value="">Primary Use Case</option>
                  {useCaseOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Goals & Contact</h3>
            <div className="form-group">
              <label htmlFor="goals">Goals</label>
              <textarea
                id="goals"
                name="goals"
                value={formData.goals}
                onChange={handleInputChange}
                placeholder="What are your main goals?"
                rows={3}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="notes">Personal Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Add any personal notes or reminders..."
                rows={4}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone Number"
                />
              </div>
              <div className="form-group">
                <label htmlFor="linkedinUrl">LinkedIn URL</label>
                <input
                  type="url"
                  id="linkedinUrl"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                  placeholder="LinkedIn URL"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Preferences</h3>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: 'row' }}>
              <input
                type="checkbox"
                id="dailySummaryEnabled"
                name="dailySummaryEnabled"
                checked={formData.dailySummaryEnabled}
                onChange={handleInputChange}
              />
          <label htmlFor="dailySummaryEnabled" style={{ margin: 0 }}>
            Receive daily updates via text message
          </label>
            </div>
          </div>

          <div className="form-section">
            <h3>API Access</h3>
            <SessionToken />
          </div>

          <div className="form-section">
            <h3>Integrations</h3>
            <div className="form-group">
              <label>Outlook Email</label>
              {outlookStatus === null ? (
                <div style={{ padding: '8px 0', color: '#6b7280' }}>Checking connection status...</div>
              ) : outlookStatus.connected ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <CheckCircle size={18} color="#22c55e" />
                    <span style={{ color: '#166534', fontWeight: 500 }}>
                      Connected{outlookStatus.email ? ` as ${outlookStatus.email}` : ''}
                    </span>
                  </div>
                  {outlookStatus.needsReconnect ? (
                    <div style={{ padding: '8px 12px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a', color: '#92400e', fontSize: '14px' }}>
                      Connection needs to be refreshed. Please reconnect.
                    </div>
                  ) : null}
                  <button
                    onClick={handleDisconnectOutlook}
                    disabled={outlookLoading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: outlookLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      opacity: outlookLoading ? 0.6 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: 'fit-content'
                    }}
                  >
                    <XCircle size={16} />
                    {outlookLoading ? 'Disconnecting...' : 'Disconnect Outlook'}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                    <XCircle size={18} color="#ef4444" />
                    <span style={{ color: '#991b1b', fontWeight: 500 }}>Not connected</span>
                  </div>
                  <button
                    onClick={handleConnectOutlook}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      width: 'fit-content'
                    }}
                  >
                    <Mail size={16} />
                    Connect to Outlook
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>
      
      {/* Floating Save Button */}
      <button 
        className={`save-button floating ${hasChanges ? 'has-changes' : ''}`}
        onClick={handleSave}
        disabled={loading}
      >
        <span>{loading ? 'Saving...' : 'Save Changes'}</span>
        <Save size={16} />
      </button>
    </div>
  );
};

export default ProfileSettings;
